import {
  DataTableDataItem,
  IDataTable,
  IElementDataTableProperty,
} from '@shared/models/data-table.model';
import { IElementProperty } from '@shared/models/element-property.model';
import { OneOfPropertyElements } from '@shared/models/project.model';
import {
  PropertyChange,
  isSelectPropertyOptionValue,
  getCount,
  setElementProperties,
  IPropertiesOrPropertyParent,
  applyElementPropertyChanges,
} from '../element-property.helpers';
import { formatExpressionVariableName } from '../expression-variables.helpers';
import { sortByLatestUpdatedAtFn } from '../sort.helpers';
import { guards } from '../typeguards.helpers';
import {
  getConnectedDataTablesIdsFromElement,
  getPropertiesByDataTableId,
  isDataTableMainProperty,
  isDataTableChildProperty,
} from './data-table.helpers';
import { ItemOrItemId } from '@shared/models/type-helpers.model';

/** Thrown when an element references data tables that are not in the enrich dependency set. */
export class MissingConnectedDataTablesError extends Error {
  override readonly name = 'MissingConnectedDataTablesError';

  constructor(
    public readonly missingTableIds: string[],
    public readonly elementId?: string,
  ) {
    super(
      `Data tables required for enrichment are unavailable: ${missingTableIds.join(', ')}`,
    );
  }
}

/**
 * Ids of data tables connected on {@link element} that are absent from {@link dataTables}.
 */
export const getMissingConnectedDataTableIds = (
  element: OneOfPropertyElements,
  dataTables: IDataTable[],
): string[] => {
  const connectedIds = getConnectedDataTablesIdsFromElement(element);
  if (!connectedIds.length) return [];

  const availableIds = new Set(dataTables.map((table) => table.id));
  return connectedIds.filter((id) => !availableIds.has(id));
};

/**
 * Fail fast when {@link element} has connected data tables that cannot be resolved.
 */
export const assertConnectedDataTablesAvailable = (
  element: OneOfPropertyElements,
  dataTables: IDataTable[],
): void => {
  const missingTableIds = getMissingConnectedDataTableIds(element, dataTables);
  if (!missingTableIds.length) return;

  throw new MissingConnectedDataTablesError(missingTableIds, element.id);
};

/**
 * Sync all data table properties from the data tables connected to the element
 * @param element
 * @param dataTables
 * @returns
 */
export const syncDataTableProperties = <T extends OneOfPropertyElements>(
  element: T,
  dataTables: IDataTable[],
): T => {
  const tableIds = getConnectedDataTablesIdsFromElement(element);
  if (!tableIds.length) return element;

  assertConnectedDataTablesAvailable(element, dataTables);

  for (const id of tableIds) {
    const dataTable = dataTables.find((table) => table.id === id);
    if (dataTable) {
      element = syncDataTablePropertiesWithDataTable(element, dataTable);
    }
  }
  return element;
};

const syncDataTablePropertiesWithDataTable = <T extends OneOfPropertyElements>(
  element: T,
  dataTable: IDataTable,
): T => {
  // User changed main property => clear all child property selections and apply values from data as fallbacks
  if (shouldClearChildProperties(element, dataTable)) {
    return setDataTablePropertiesOnElementByDataItem(element, dataTable);
  }
  const dataItem = getDataItemFromChildPropertySelections(element, dataTable);
  if (!dataItem) {
    console.error(
      `JSON item not found for data table ${dataTable.name} (${dataTable.id})`,
    );
    return element;
  }

  return setDataTablePropertiesOnElementByDataItem(
    element,
    dataTable,
    dataItem,
  );
};

/**
 * Detect if user have modified the main property and we should clear the child properties
 * @param element
 * @param dataTable
 * @returns
 */
const shouldClearChildProperties = (
  element: OneOfPropertyElements,
  dataTable: IDataTable,
): boolean => {
  const lastModifiedChild = getPropertiesByDataTableId(element, dataTable).sort(
    sortByLatestUpdatedAtFn,
  )[0];
  return (
    isDataTableMainProperty(lastModifiedChild) &&
    guards.is.defined(lastModifiedChild.count)
  );
};

/**
 * Clear all child properties and apply values from selected data item as fallbacks
 * @param element
 * @param dataTable
 * @param childDataItemSelection Provide data item to override the default one (selected json data item by main property count)
 * @returns
 */
const setDataTablePropertiesOnElementByDataItem = <
  T extends OneOfPropertyElements,
>(
  element: T,
  dataTable: IDataTable,
  childDataItemSelection?: Record<string, unknown>,
): T => {
  // Set the properties
  return setElementProperties(
    element,
    element,
    ...getDataTablePropertiesChangesFromDataItem(
      element,
      dataTable,
      childDataItemSelection,
    ),
  );
};

export const setDataTablePropertiesByDataItem = (
  properties: IElementProperty[],
  dataTable: IDataTable,
  childDataItemSelection?: Record<string, unknown>,
): IElementProperty[] => {
  // Set the properties
  return applyElementPropertyChanges(
    properties,
    ...getDataTablePropertiesChangesFromDataItem(
      properties,
      dataTable,
      childDataItemSelection,
    ),
  );
};

const getDataTablePropertiesChangesFromDataItem = (
  elementOrProps: OneOfPropertyElements | IPropertiesOrPropertyParent,
  dataTable: IDataTable,
  childDataItemSelection?: Record<string, unknown>,
): PropertyChange[] => {
  const childProperties = getDataTableChildPropertiesFromElement(
    elementOrProps,
    dataTable,
  );
  const changes: PropertyChange[] = [];

  // If we want to override the selected data item we should clear the main property count and set the fallback count to the value from the data item
  if (childDataItemSelection) {
    const mainProperty = getDataTableMainPropertyFromElement(
      elementOrProps,
      dataTable,
    );
    if (!mainProperty) {
      console.error(
        `Main property not found for data table ${dataTable.name} (${dataTable.id})`,
      );
      return changes;
    }
    const value = childDataItemSelection[dataTable.index_key];
    changes.push({
      id: mainProperty.id,
      count: undefined,
      fallbackCount: isSelectPropertyOptionValue(mainProperty, value)
        ? value
        : mainProperty.fallbackCount,
    });
  }

  // Fallback on data item selected by main property count
  const dataItem =
    childDataItemSelection ??
    getDataItemByMainPropertyCount(elementOrProps, dataTable);

  if (!dataItem) {
    console.error(
      `JSON item not found for data table ${dataTable.name} (${dataTable.id})`,
    );
    return changes;
  }

  // Check all keys in data item and try to update childproperties with the values
  Object.entries(dataItem).forEach(([key, value]) => {
    // Find the child property that matches the key
    const property = childProperties.find(
      (property) => property.name === formatExpressionVariableName(key),
    );

    // All values in data item does not need to have a property since we select fields to include when setting up the data table
    if (!property) return;

    // Keep count if it's already selected
    const count = property.count === value ? property.count : undefined;
    if (
      isSelectPropertyOptionValue(property, value) &&
      value !== property.fallbackCount
    ) {
      changes.push({
        id: property.id,
        count,
        fallbackCount: value,
      });
    }
  });

  return changes;
};

/**
 * Get the dataTable main property from an element by data table or id.
 * Only one main property per data table.
 * @param element
 * @param dataTableOrId
 * @returns
 */
const getDataTableMainPropertyFromElement = (
  elementOrProps: OneOfPropertyElements | IPropertiesOrPropertyParent,
  dataTableOrId: ItemOrItemId<IDataTable>,
): IElementDataTableProperty | undefined => {
  return getPropertiesByDataTableId(elementOrProps, dataTableOrId).find(
    isDataTableMainProperty,
  );
};

/**
 * Get the dataTable child properties from an element by data table or id
 * @param element
 * @param dataTableOrId
 * @returns
 */
const getDataTableChildPropertiesFromElement = (
  elementOrProps: OneOfPropertyElements | IPropertiesOrPropertyParent,
  dataTableOrId: ItemOrItemId<IDataTable>,
): IElementDataTableProperty[] => {
  return getPropertiesByDataTableId(elementOrProps, dataTableOrId).filter(
    isDataTableChildProperty,
  );
};

/**
 * Get the data item from the data table by the main property count
 * @param element
 * @param dataTable
 * @returns
 */
const getDataItemByMainPropertyCount = (
  elementOrProps: OneOfPropertyElements | IPropertiesOrPropertyParent,
  dataTable: IDataTable,
): DataTableDataItem | undefined => {
  const mainProperty = getDataTableMainPropertyFromElement(
    elementOrProps,
    dataTable,
  );
  if (!mainProperty) {
    return undefined;
  }
  return dataTable.data.find(
    (item) => item[dataTable.index_key] === getCount(mainProperty),
  );
};

const getDataKeyFromPropertyName = (
  dataTable: IDataTable,
  property: IElementProperty,
): string | undefined => {
  return dataTable.properties.find(
    (key) => formatExpressionVariableName(key) === property.name,
  );
};

/**
 * Get the data item from the data table by the child property selections
 * @param element
 * @param dataTable
 * @returns
 */
export const getDataItemFromChildPropertySelections = (
  element: OneOfPropertyElements,
  dataTable: IDataTable,
): Record<string, unknown> | undefined => {
  const { id, data } = dataTable;
  const childProperties = getPropertiesByDataTableId(element, id)
    .sort(sortByLatestUpdatedAtFn)
    .filter(isDataTableChildProperty);

  // Get all active user selections in order
  const userSelections = childProperties
    .map((property) => {
      const name = getDataKeyFromPropertyName(dataTable, property);
      const value = property.count;
      if (name && value) {
        return {
          name: property.name,
          value,
        };
      }
    })
    .filter(guards.is.defined);

  if (!userSelections.length) {
    return undefined;
  }

  let matches = data;

  for (const selection of userSelections) {
    const newMatches = matches.filter(
      (item) => item[selection.name] === selection.value,
    );
    if (!newMatches.length) {
      break;
    }
    matches = newMatches;
  }
  return matches[0];
};
